/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { inject as service } from '@ember/service';

import { post } from 'embercom/lib/ajax';

import type TicketsIntegration from 'embercom/models/standalone/freshdesk-tickets-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { IntegrationState } from 'embercom/objects/standalone/constants';

export default class FreshdeskConfiguration extends Model {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: AppService;
  @attr('string') declare subdomain: string;
  @attr('string') declare apiKey: string;
  @attr('number', { defaultValue: IntegrationState.NotConfigured })
  declare ticketState: IntegrationState;

  @belongsTo('standalone/freshdesk-tickets-configuration', { async: false })
  declare ticketsIntegration: TicketsIntegration;

  get canUpdateAPISettings() {
    return this.subdomain && this.apiKey;
  }

  get isConnected() {
    return !this.isPendingSetupForTickets;
  }

  get isEnabledForTickets() {
    return this.ticketState === IntegrationState.Enabled;
  }

  get isPausedForTickets() {
    return this.ticketState === IntegrationState.Paused;
  }

  get isDisabledForTickets() {
    return (
      this.ticketState === IntegrationState.Paused ||
      this.ticketState === IntegrationState.Created ||
      this.ticketState === IntegrationState.NotConfigured
    );
  }

  get isPendingSetupForTickets() {
    return this.ticketState === IntegrationState.NotConfigured;
  }

  get canRevokeTicketsApi() {
    // For Freshdesk, we only check if tickets are disabled
    return !this.isEnabledForTickets;
  }

  get isFinEnabledForIntercomMessenger() {
    return this.finStandaloneService.intercomMessengerConfig.isMessengerEnabled;
  }

  get adminBaseUrl(): string | undefined {
    if (!this.subdomain) {
      return undefined;
    }

    return `https://${this.subdomain}.freshdesk.com`;
  }

  async revoke() {
    await post(`/ember/standalone/freshdesk_configurations/${this.id}/revoke`);
    this.apiKey = '';
    await this.reload();
  }

  async activateForTickets() {
    await post(`/ember/standalone/freshdesk_configurations/${this.id}/activate_for_tickets`);
    await this.reload();
  }

  async deactivateForTickets() {
    await post(`/ember/standalone/freshdesk_configurations/${this.id}/deactivate_for_tickets`);
    await this.reload();
  }
}
